import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Alert,
  PageContentOffers,
  Sheet,
  SheetContentProductSelection,
  SheetContentProduct,
  OptionRow,
  useRestaurantStore,
  useCartStore,
  useOrderStore,
  useAuthStore,
  OfferWarningType,
  validateOffer,
  validateOfferTimeConstraints,
  useRewardsStore,
  matchCartToOfferProducts,
  applyOfferToCartGated,
  requiresCode,
  isOfferCodeUnsatisfied,
  SheetContentOffer,
} from '@restaurant/core'
import type { TileOfferProps, OfferProduct, MenuItem } from '@restaurant/core'
import type { ProductChoicesItem, OfferValidationStatus } from '@restaurant/core'
import { useOfferAlerts } from '../hooks/useOfferAlerts'
import { useWebOffersPageViewModel } from '../viewmodels/pages/WebOffersPageViewModel'
import { useWebOffersViewModel } from '../viewmodels/sheets/WebOffersViewModel'
import { showcaseBlocked } from '../showcase/guard'
import { generateOfferTerms, getRemainingUsesText } from '@restaurant/core'

function mapValidationToWarning(status: OfferValidationStatus): OfferWarningType | null {
  switch (status) {
    case 'conflicts_with_reward': return OfferWarningType.rewardAndOfferNotAllowed
    case 'another_offer_applied': return OfferWarningType.oneOfferAllowedPerOrder
    case 'minimum_purchase_not_met': return OfferWarningType.minimumOrderNotMet
    case 'required_products_missing': return OfferWarningType.missingRequiredItem
    case 'store_location_not_supported': return OfferWarningType.notValidAtSelectedLocation
    case 'outside_time_constraints': return OfferWarningType.offerNotAvailableAtThisTime
    case 'expired': return OfferWarningType.offerNoLongerValid
    case 'upcoming': return OfferWarningType.offerNoLongerValid
    case 'usage_limit_reached': return OfferWarningType.offerNoLongerValid
    default: return null
  }
}

export default function OffersPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const pageVM = useWebOffersPageViewModel()
  const offersVM = useWebOffersViewModel()

  const offerAlert = useOfferAlerts()
  const appliedOfferId = useCartStore((s) => s.appliedOfferId)
  const removeAppliedOffer = useCartStore((s) => s.removeAppliedOffer)
  const getMenuItemById = useRestaurantStore((s) => s.getMenuItemById)
  const addItem = useCartStore((s) => s.addItem)
  const activeOrder = useOrderStore((s) => s.activeOrder)
  const userId = useAuthStore((s) => s.user?.id)

  // Product detail sheet state (step 2 of the offer flow)
  const [isProductDetailOpen, setIsProductDetailOpen] = useState(false)
  const [selectedOfferMenuItem, setSelectedOfferMenuItem] = useState<MenuItem | null>(null)
  const [offerProductQuantity, setOfferProductQuantity] = useState(1)
  const [offerSelectedCustomizations, setOfferSelectedCustomizations] = useState<Record<string, Set<string>>>({})
  const [currentOfferProduct, setCurrentOfferProduct] = useState<OfferProduct | null>(null)

  const { loadOffer } = offersVM

  // Stable references for visibilitychange effect
  const currentViewedOffer = offersVM.offer
  const reloadOffer = offersVM.loadOffer

  // Whether the currently-viewed offer is already applied to the cart
  const isOfferApplied = offersVM.offer?.id != null && appliedOfferId === offersVM.offer.id

  // Flag to track when we've requested a flow start after loading an offer.
  // Uses a ref for synchronous reads (prevents stale reads inside effects when
  // Zustand updates trigger re-renders before React flushes the state update)
  // and a counter state to trigger the effect re-run.
  const pendingFlowStartRef = useRef(false)
  const [flowStartTrigger, setFlowStartTrigger] = useState(0)

  const [isOfferDetailOpen, setIsOfferDetailOpen] = useState(false)
  const restaurantName = useRestaurantStore((s) => s.currentRestaurant?.name || '')
  const merchantConfig = useRewardsStore((s) => s.merchantConfig)

  // Part A: Clear stale offer removal alerts on mount; re-enable sync on unmount
  useEffect(() => {
    useCartStore.getState().clearLastOfferRemoval()
    return () => {
      useCartStore.getState().setSuppressSync(false)
    }
  }, [])

  // Part B: Re-validate offers when browser tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && currentViewedOffer) {
        reloadOffer(currentViewedOffer.id)
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [currentViewedOffer, reloadOffer])

  // Auto-start offer flow when navigated from CartPage missing items alert.
  // loadOffer is async — offersVM.offer won't be populated yet on the first run.
  // The flowStartTrigger effect below will re-run when offersVM.offer resolves
  // (it's in that effect's dep array) and will see pendingFlowStartRef still true.
  useEffect(() => {
    const autoStartId = (location.state as { autoStartOfferId?: string })?.autoStartOfferId
    if (autoStartId && !pendingFlowStartRef.current) {
      // Clear router state to prevent re-trigger on refresh
      navigate('.', { replace: true, state: {} })

      if (offersVM.offer?.id !== autoStartId) {
        loadOffer(autoStartId)
      }
      pendingFlowStartRef.current = true
      setFlowStartTrigger((c) => c + 1)
    }
  }, [location.state, loadOffer, offersVM.offer, navigate])

  // Build a stable lookup from testID → offer ID so we don't rely on fragile
  // index-based resolution (the page VM sets testID to `offer-tile-${id}`).
  const offerIdByTestID = useMemo(() => {
    const map = new Map<string, string>()
    for (const summary of pageVM.allOffers) {
      map.set(`offer-tile-${summary.id}`, summary.id)
    }
    return map
  }, [pageVM.allOffers])

  const handleOfferClick = useCallback(
    (offer: TileOfferProps, _groupIndex: number, _offerIndex: number, _groupId?: string) => {
      if (showcaseBlocked()) return
      if (activeOrder) {
        offerAlert.showWarning(OfferWarningType.orderInProgress, null, {})
        return
      }
      const targetOfferId = offer.testID ? offerIdByTestID.get(offer.testID) : undefined
      if (targetOfferId) {
        loadOffer(targetOfferId)
        setIsOfferDetailOpen(true)
      }
    },
    [offerIdByTestID, loadOffer, activeOrder, offerAlert]
  )

  const detailSheetButton = useMemo(() => {
    if (!offersVM.offer) return { text: 'ADD TO ORDER', variant: 'Primary' as const, disabled: true }

    if (!userId) {
      return { text: 'LOGIN TO CLAIM', variant: 'Primary' as const, disabled: false }
    }
    const timeCheck = validateOfferTimeConstraints(offersVM.offer)
    if (timeCheck.status !== 'valid') {
      return { text: 'OFFER UNAVAILABLE', variant: 'Unavailable' as const, disabled: false }
    }
    if (isOfferApplied) {
      return { text: 'REMOVE OFFER', variant: 'Warning' as const, disabled: false }
    }
    // Disable ADD until a required 6-digit code is fully entered (OR-combined with
    // the existing disabled value, which is false in this add branch).
    const needsCode = requiresCode(offersVM.offer)
    const codeUnsatisfied = needsCode && (offersVM.enteredCode?.replace(/\D/g, '').length ?? 0) !== 6
    return { text: 'ADD TO ORDER', variant: 'Primary' as const, disabled: false || codeUnsatisfied }
  }, [offersVM.offer, offersVM.enteredCode, isOfferApplied, userId])

  const handleDetailSheetButtonPress = useCallback(() => {
    if (!offersVM.offer) return

    // Auth gate: redirect guest users to login
    if (!userId) {
      setIsOfferDetailOpen(false)
      navigate('/menu')
      return
    }

    // Code gate (defense-in-depth; the button is also disabled until 6 digits).
    // Keep the sheet open on mismatch so the inline error is visible.
    if (isOfferCodeUnsatisfied(offersVM.offer, offersVM.enteredCode)) {
      offersVM.setCodeError('Incorrect code')
      return
    }
    offersVM.setCodeError(null)

    setIsOfferDetailOpen(false)

    const timeCheck = validateOfferTimeConstraints(offersVM.offer)
    if (timeCheck.status !== 'valid') {
      return
    }

    if (isOfferApplied) {
      removeAppliedOffer()
      return
    }

    pendingFlowStartRef.current = true
    setFlowStartTrigger((c) => c + 1)
  }, [offersVM.offer, offersVM.enteredCode, offersVM.setCodeError, isOfferApplied, removeAppliedOffer, userId, navigate])

  // Watch for offer load completion and validate before starting the flow
  useEffect(() => {
    if (!pendingFlowStartRef.current || !offersVM.offer || offersVM.offerFlowState !== 'idle') return
    // Clear ref synchronously BEFORE any store updates to prevent re-entry
    pendingFlowStartRef.current = false

      // A code-required offer must not auto-apply from a deep link / cart banner —
      // open the detail so the customer can enter the code. This guard lives in the
      // flowStartTrigger effect (not autoStartOfferId) because offersVM.offer is only
      // populated here, after the async loadOffer resolves.
      if (isOfferCodeUnsatisfied(offersVM.offer, offersVM.enteredCode)) {
        setIsOfferDetailOpen(true)
        return
      }

      // Pre-apply validation
      const cartState = useCartStore.getState()
      const rewardsState = useRewardsStore.getState()
      const subtotal = cartState.items.reduce((sum: number, item) => sum + item.totalPrice, 0)
      const rewardsApplied = (rewardsState.appliedRewardDetails?.length ?? 0) > 0

      const result = validateOffer(offersVM.offer, {
        subtotal,
        cartItems: cartState.items,
        currentStoreId: useRestaurantStore.getState().currentLocation?.id || cartState.restaurantId || '',
        appliedOfferId: cartState.appliedOfferId,
        appliedRewardId: rewardsApplied ? 'has-active-reward' : null,
        merchantRewardConfig: rewardsState.merchantConfig || undefined,
        skipProductCheck: true, // matchCartToOfferProducts handles missing products
      })

      if (result.status !== 'valid') {
        const isSameOfferReapply = result.status === 'another_offer_applied' && cartState.appliedOfferId === offersVM.offer.id
        if (!isSameOfferReapply) {
          const warningType = mapValidationToWarning(result.status)
          if (warningType) {
            const offer = offersVM.offer
            offerAlert.showWarning(warningType, offer, {
              onPrimary: () => {
                // For minimum purchase — apply offer at $0 discount (user can add items later).
                // Routed through the gated funnel so a code-required offer still can't slip in.
                if (warningType === OfferWarningType.minimumOrderNotMet) {
                  const r = applyOfferToCartGated(offer.id, offersVM.enteredCode, { discountOverride: 0 })
                  if (!r.ok && r.reason === 'code_mismatch') offersVM.setCodeError('Incorrect code')
                }
              },
              onSecondary: () => {
                // For "one offer per order" — remove old, retry
                if (warningType === OfferWarningType.oneOfferAllowedPerOrder) {
                  removeAppliedOffer()
                  loadOffer(offer.id)
                  pendingFlowStartRef.current = true; setFlowStartTrigger((c) => c + 1)
                }
                // For reward conflict — remove rewards, retry
                if (warningType === OfferWarningType.rewardAndOfferNotAllowed) {
                  const rs = useRewardsStore.getState()
                  for (const reward of rs.appliedRewardDetails) {
                    rs.removePendingRedemption(reward.groupId)
                  }
                  loadOffer(offer.id)
                  pendingFlowStartRef.current = true; setFlowStartTrigger((c) => c + 1)
                }
              },
            }, {
              minimumPurchase: offer.minimumPurchase,
            })
            return
          }
        }
      }

      // Pre-flow cart matching: check if required/free items are already in cart
      const matchResult = matchCartToOfferProducts(
        offersVM.offer,
        cartState.items,
        cartState.freeItemIds
      )

      // If everything is already satisfied, apply offer directly through the gated
      // funnel. The funnel performs the markItemAsFree + calculateDiscount +
      // setAppliedOffer that used to live inline here, and enforces the code gate.
      if (
        matchResult.missingRequiredProducts.length === 0 &&
        matchResult.missingFreeProducts.length === 0
      ) {
        const r = applyOfferToCartGated(offersVM.offer.id, offersVM.enteredCode)
        if (!r.ok && r.reason === 'code_mismatch') offersVM.setCodeError('Incorrect code')
        return
      }

      // Missing products — auto-converted items will be marked free at apply time
      // (in the service's applyOfferToCart) when the flow completes successfully.

      // Suppress draft sync during offer flow — items sync on completion
      useCartStore.getState().setSuppressSync(true)

      // Start flow with only missing products
    offersVM.startOfferFlow({
      missingRequired: matchResult.missingRequiredProducts,
      missingFree: matchResult.missingFreeProducts,
    })
  }, [flowStartTrigger, offersVM.offer, offersVM.offerFlowState, offersVM.startOfferFlow, offerAlert, removeAppliedOffer, loadOffer])

  // Determine which products to show based on the current flow state
  const currentProducts: OfferProduct[] = useMemo(() => {
    if (offersVM.offerFlowState === 'required_products') {
      return offersVM.remainingRequiredProducts
    }
    if (offersVM.offerFlowState === 'free_products') {
      return offersVM.remainingFreeProducts
    }
    return []
  }, [offersVM.offerFlowState, offersVM.remainingRequiredProducts, offersVM.remainingFreeProducts])

  // Whether the offer flow product selection sheet should be open
  // (only when we're NOT in the product detail sub-step)
  const isOfferFlowSheetOpen =
    (offersVM.offerFlowState === 'required_products' ||
      offersVM.offerFlowState === 'free_products') &&
    !isProductDetailOpen

  // Convert OfferProducts to ProductChoicesItem[] for SheetContentProductSelection
  const isFreeStep = offersVM.offerFlowState === 'free_products'
  const offerDiscountPct = (offersVM.pendingOffer || offersVM.offer)?.value ?? 100
  const productChoices: ProductChoicesItem[] = useMemo(() => {
    return currentProducts.map((offerProduct) => {
      const menuItem = getMenuItemById(offerProduct.menuItemId)
      const price = menuItem ? `$${menuItem.price.toFixed(2)}` : '$0.00'
      if (!isFreeStep) {
        return {
          id: offerProduct.menuItemId,
          productName: menuItem?.name ?? offerProduct.menuItemId,
          priceCurrent: price,
          showImageProductButton: !!menuItem?.imageUrl,
          imageUrl: menuItem?.imageUrl,
        }
      }
      // Free step: show discount based on offer value (100% = FREE, <100% = discounted price)
      const isFullyFree = offerDiscountPct >= 100
      const discountedPrice = menuItem ? `$${(menuItem.price * (1 - offerDiscountPct / 100)).toFixed(2)}` : '$0.00'
      return {
        id: offerProduct.menuItemId,
        productName: menuItem?.name ?? offerProduct.menuItemId,
        priceCurrent: isFullyFree ? 'FREE' : discountedPrice,
        priceOld: price,
        showPriceOld: true,
        showImageProductButton: !!menuItem?.imageUrl,
        imageUrl: menuItem?.imageUrl,
      }
    })
  }, [currentProducts, getMenuItemById, isFreeStep, offerDiscountPct])

  // Whether this is a single-product step (auto-open product detail)
  const isSingleProduct = currentProducts.length === 1

  // Reset product detail state
  const resetProductDetail = useCallback(() => {
    setIsProductDetailOpen(false)
    setSelectedOfferMenuItem(null)
    setOfferProductQuantity(1)
    setOfferSelectedCustomizations({})
    setCurrentOfferProduct(null)
  }, [])

  // Open product detail sheet for a selected menu item
  const openProductDetail = useCallback((menuItem: MenuItem, offerProduct?: OfferProduct) => {
    setSelectedOfferMenuItem(menuItem)
    setOfferProductQuantity(1)
    if (offerProduct) setCurrentOfferProduct(offerProduct)

    // Determine variant restrictions from the offer product
    const allowedVariants = offerProduct?.variants ?? []
    const hasVariantRestriction =
      allowedVariants.length > 0 &&
      !(allowedVariants.length === 1 && allowedVariants[0]?.toLowerCase() === 'base')

    // Initialize customizations with defaults (same pattern as MenuPage)
    const initialCustomizations: Record<string, Set<string>> = {}
    menuItem.customizations?.forEach(customization => {
      const defaultOptions = customization.options
        .filter(opt => opt.isDefault)
        .map(opt => opt.id)
      if (defaultOptions.length > 0) {
        initialCustomizations[customization.id] = new Set(defaultOptions)
      }
    })

    // Auto-select single remaining variant when filtering leaves only one option
    if (hasVariantRestriction && menuItem.customizations) {
      for (const customization of menuItem.customizations) {
        const matchingOptions = customization.options.filter((opt) =>
          allowedVariants.some((v) => v.toLowerCase() === opt.name.toLowerCase())
        )
        const onlyOption = matchingOptions[0]
        if (matchingOptions.length === 1 && onlyOption) {
          initialCustomizations[customization.id] = new Set([onlyOption.id])
        }
      }
    }

    setOfferSelectedCustomizations(initialCustomizations)

    setIsProductDetailOpen(true)
  }, [])

  // Handle product selection from the selection grid (step 1)
  // This opens the product detail sheet (step 2)
  const handleProductSelect = useCallback(
    (product: ProductChoicesItem, _index: number) => {
      const menuItem = getMenuItemById(product.id)
      if (!menuItem) return

      // Find the matching OfferProduct to get variant restrictions
      const offerProduct = currentProducts.find((p) => p.menuItemId === product.id)
      setCurrentOfferProduct(offerProduct || null)

      openProductDetail(menuItem, offerProduct)
    },
    [getMenuItemById, openProductDetail, currentProducts]
  )

  // Handle customization option changes (same pattern as MenuPage)
  const handleOfferOptionChange = useCallback((customizationId: string, optionId: string, selected: boolean, isRadio: boolean) => {
    setOfferSelectedCustomizations(prev => {
      const newSelections = { ...prev }

      if (isRadio) {
        newSelections[customizationId] = new Set([optionId])
      } else {
        const currentSet = new Set(prev[customizationId] || [])
        if (selected) {
          currentSet.add(optionId)
        } else {
          currentSet.delete(optionId)
        }
        newSelections[customizationId] = currentSet
      }

      return newSelections
    })
  }, [])

  // Calculate total price for the offer product detail
  const offerProductTotalPrice = useMemo(() => {
    if (!selectedOfferMenuItem) return 0

    const basePrice = selectedOfferMenuItem.price
    let customizationCost = 0

    selectedOfferMenuItem.customizations?.forEach(customization => {
      const selections = offerSelectedCustomizations[customization.id] || new Set()
      selections.forEach(optionId => {
        const option = customization.options.find(opt => opt.id === optionId)
        if (option) {
          customizationCost += option.price
        }
      })
    })

    return (basePrice + customizationCost) * offerProductQuantity
  }, [selectedOfferMenuItem, offerSelectedCustomizations, offerProductQuantity])

  // Calculate missing required customizations for the offer product
  const offerMissingRequirements = useMemo(() => {
    if (!selectedOfferMenuItem?.customizations) return undefined

    const missingCount = selectedOfferMenuItem.customizations.filter(customization => {
      if (!customization.isRequired) return false
      const selections = offerSelectedCustomizations[customization.id] || new Set()
      return selections.size === 0
    }).length

    if (missingCount === 0) return undefined
    if (missingCount === 1) return 'Select 1 required option'
    return `Select ${missingCount} required options`
  }, [selectedOfferMenuItem, offerSelectedCustomizations])

  // Build option groups for the offer product detail (same pattern as MenuPage)
  // Filters variant options when the currentOfferProduct has variant restrictions
  const offerOptionGroups = useMemo(() => {
    if (!selectedOfferMenuItem?.customizations) return []

    const allowedVariants = currentOfferProduct?.variants ?? []
    const hasVariantRestriction =
      allowedVariants.length > 0 &&
      !(allowedVariants.length === 1 && allowedVariants[0]?.toLowerCase() === 'base')

    return selectedOfferMenuItem.customizations.map((customization) => {
      const isRadio = customization.maxSelections === 1
      const customizationSelections = offerSelectedCustomizations[customization.id] || new Set()

      // Filter options if variant restriction applies to this group
      let visibleOptions = customization.options
      if (hasVariantRestriction) {
        const groupHasVariantMatch = customization.options.some((opt) =>
          allowedVariants.some((v) => v.toLowerCase() === opt.name.toLowerCase())
        )
        if (groupHasVariantMatch) {
          visibleOptions = customization.options.filter((opt) =>
            allowedVariants.some((v) => v.toLowerCase() === opt.name.toLowerCase())
          )
        }
      }

      return {
        id: customization.id,
        title: customization.name,
        required: customization.isRequired,
        maxSelections: customization.maxSelections,
        children: (
          <>
            {visibleOptions.map((option) => (
              <OptionRow
                key={option.id}
                text={`${option.name}${option.price > 0 ? ` (+$${option.price.toFixed(2)})` : ''}`}
                optionType={isRadio ? 'radio' : 'checkbox'}
                value={option.id}
                name={customization.id}
                selected={customizationSelections.has(option.id)}
                onChange={(value, selected) => {
                  handleOfferOptionChange(customization.id, value, selected, isRadio)
                }}
              />
            ))}
          </>
        ),
      }
    })
  }, [selectedOfferMenuItem, offerSelectedCustomizations, handleOfferOptionChange, currentOfferProduct])

  // Handle "Remove Offer" when the current offer is already applied
  const handleOfferRemove = useCallback(() => {
    removeAppliedOffer()
    resetProductDetail()
    if (offersVM.offerFlowState !== 'idle') {
      offersVM.cancelOfferFlow()
    }
  }, [removeAppliedOffer, resetProductDetail, offersVM])

  // Handle "Add to Order" from the product detail sheet (step 2 completion)
  const handleOfferAddToOrder = useCallback(() => {
    if (!selectedOfferMenuItem) return

    // Convert selectedCustomizations to the format cart expects
    const customizationSelections = Array.from(
      Object.entries(offerSelectedCustomizations)
    ).flatMap(([customizationItemId, optionIds]) =>
      Array.from(optionIds).map(selectedOptionId => ({
        customizationItemId,
        selectedOptionId,
      }))
    )

    // Determine the selected variant from customization selections
    let selectedPriceVariant: string | undefined
    if (selectedOfferMenuItem?.customizations && currentOfferProduct?.variants?.length) {
      for (const customization of selectedOfferMenuItem.customizations) {
        const selections = offerSelectedCustomizations[customization.id] || new Set()
        if (selections.size === 1) {
          const selectedId = Array.from(selections)[0]
          const option = customization.options.find((o) => o.id === selectedId)
          if (option && currentOfferProduct.variants.some(
            (v) => v.toLowerCase() === option.name.toLowerCase()
          )) {
            selectedPriceVariant = option.name
            break
          }
        }
      }
    }

    // Add to cart and capture the returned cart item ID
    const cartItemId = addItem(selectedOfferMenuItem, offerProductQuantity, customizationSelections, { selectedPriceVariant })

    // Advance the offer flow with the real cart item ID
    if (offersVM.offerFlowState === 'required_products') {
      offersVM.onRequiredProductAdded(selectedOfferMenuItem.id, cartItemId)
    } else if (offersVM.offerFlowState === 'free_products') {
      // Mark the item as free in the cart before advancing
      useCartStore.getState().markItemAsFree(cartItemId, offersVM.pendingOffer?.id || offersVM.offer?.id || '')
      offersVM.onFreeProductAdded(selectedOfferMenuItem.id, cartItemId)
    }

    // Close product detail and reset
    resetProductDetail()
  }, [selectedOfferMenuItem, offerSelectedCustomizations, offerProductQuantity, addItem, offersVM, resetProductDetail, currentOfferProduct])

  // Handle cancel from product detail — cancel the entire offer flow and roll back items
  const handleProductDetailClose = useCallback(() => {
    if (offersVM.offerFlowState !== 'idle' && offersVM.offerFlowState !== 'complete') {
      const cartStore = useCartStore.getState()
      for (const itemId of offersVM.itemsAddedDuringFlow) {
        cartStore.removeItem(itemId)
      }
      cartStore.setSuppressSync(false)
      offersVM.cancelOfferFlow()
    }
    resetProductDetail()
  }, [offersVM.offerFlowState, offersVM.itemsAddedDuringFlow, offersVM.cancelOfferFlow, resetProductDetail])

  // Handle going back one step in the flow (e.g., free_products → required_products)
  // Removes the last item added in the previous step and re-evaluates the flow
  const handleFlowGoBack = useCallback(() => {
    const currentOffer = offersVM.pendingOffer || offersVM.offer
    if (!currentOffer) return
    const cartStore = useCartStore.getState()

    // Remove only the last item added during the flow
    const addedItems = offersVM.itemsAddedDuringFlow
    const lastItemId = addedItems[addedItems.length - 1]
    if (lastItemId) {
      cartStore.removeItem(lastItemId)
    }

    // Re-load the offer to ensure it's set on the ViewModel, then restart flow
    loadOffer(currentOffer.id).then(() => {
      const updatedCart = useCartStore.getState()
      const matchResult = matchCartToOfferProducts(
        currentOffer,
        updatedCart.items,
        updatedCart.freeItemIds
      )
      offersVM.startOfferFlow({
        missingRequired: matchResult.missingRequiredProducts,
        missingFree: matchResult.missingFreeProducts,
      })
    })
  }, [offersVM, loadOffer])

  // Handle cancel from the selection sheet — cancel the entire offer flow
  // Roll back any items added during the flow before resetting state
  const handleOfferFlowCancel = useCallback(() => {
    const cartStore = useCartStore.getState()
    for (const itemId of offersVM.itemsAddedDuringFlow) {
      cartStore.removeItem(itemId)
    }
    cartStore.setSuppressSync(false)
    resetProductDetail()
    offersVM.cancelOfferFlow()
  }, [offersVM, resetProductDetail])

  // Auto-open product detail for single-product steps
  useEffect(() => {
    const shouldAutoOpen =
      isSingleProduct &&
      (offersVM.offerFlowState === 'required_products' || offersVM.offerFlowState === 'free_products') &&
      !isProductDetailOpen

    if (shouldAutoOpen && currentProducts[0]) {
      const offerProduct = currentProducts[0]
      setCurrentOfferProduct(offerProduct)
      const menuItem = getMenuItemById(offerProduct.menuItemId)
      if (menuItem) {
        // Skip product detail for items with no customizations
        const hasCustomizations = menuItem.customizations && menuItem.customizations.length > 0
        if (!hasCustomizations) {
          const cartItemId = addItem(menuItem, 1, [])

          if (offersVM.offerFlowState === 'required_products') {
            offersVM.onRequiredProductAdded(menuItem.id, cartItemId)
          } else if (offersVM.offerFlowState === 'free_products') {
            useCartStore.getState().markItemAsFree(cartItemId, offersVM.pendingOffer?.id || offersVM.offer?.id || '')
            offersVM.onFreeProductAdded(menuItem.id, cartItemId)
          }
          return // Skip opening the detail sheet
        }

        openProductDetail(menuItem, offerProduct)
      }
    }
  }, [isSingleProduct, offersVM.offerFlowState, isProductDetailOpen, currentProducts, getMenuItemById, openProductDetail, addItem, offersVM.onRequiredProductAdded, offersVM.onFreeProductAdded, offersVM.pendingOffer, offersVM.offer])

  // Batch-sync items to draft when offer flow completes
  useEffect(() => {
    if (offersVM.offerFlowState === 'complete' && offersVM.itemsAddedDuringFlow.length > 0) {
      const cartStore = useCartStore.getState()
      cartStore.setSuppressSync(false)

      // Sync each item added during the flow to the backend draft
      const callbacks = cartStore._syncCallbacks
      if (callbacks?.onAddItem) {
        const itemsToSync = offersVM.itemsAddedDuringFlow
          .map(id => cartStore.items.find(item => item.id === id))
          .filter(Boolean)

        let currentDraftId = cartStore.draftOrderId

        const syncSequentially = async () => {
          for (const item of itemsToSync) {
            if (!item) continue
            try {
              const newDraftId = await callbacks.onAddItem!(item, currentDraftId)
              if (newDraftId && newDraftId !== currentDraftId) {
                currentDraftId = newDraftId
                cartStore.setDraftOrderId(newDraftId)
              }
            } catch {
              // Non-blocking — sync errors are logged by the service
            }
          }
        }

        syncSequentially()
      }
    }
  }, [offersVM.offerFlowState, offersVM.itemsAddedDuringFlow])

  // Section description for the SectionRequiredProduct component
  const sectionItemDescription = useMemo(() => {
    const stepNum = offersVM.currentProductIndex + 1
    const total = offersVM.totalProductsInFlow
    if (offersVM.offerFlowState === 'required_products') {
      return `Required Item - ${stepNum} of ${total}`
    }
    if (offersVM.offerFlowState === 'free_products') {
      return `Free Item - ${stepNum} of ${total}`
    }
    return ''
  }, [offersVM.offerFlowState, offersVM.currentProductIndex, offersVM.totalProductsInFlow])

  // Whether to show the section required product navigation header
  const showSectionNav = offersVM.totalProductsInFlow > 1

  // Show CHANGE ITEM when there are multiple choices for the current step
  const showChangeItem = currentProducts.length > 1

  // Sheet title
  const sheetTitle = useMemo(() => {
    if (offersVM.offerFlowState === 'required_products') {
      return 'Select Required Item'
    }
    if (offersVM.offerFlowState === 'free_products') {
      return 'Choose Your Free Item'
    }
    return 'Select Product'
  }, [offersVM.offerFlowState])

  return (
    <>
      <PageContentOffers
        offerGroups={pageVM.offerGroups}
        onOfferClick={handleOfferClick}
        showDefaultGroups={false}
        loading={pageVM.loading}
        emptyStateContent={
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            <p>No offers available right now. Check back soon!</p>
          </div>
        }
        testID="offers-page"
        accessibilityLabel="Available restaurant offers and promotions"
      />

      {/* Offer Detail Sheet */}
      <Sheet
        isOpen={isOfferDetailOpen && offersVM.offer != null}
        onClose={() => setIsOfferDetailOpen(false)}
        title={offersVM.offer?.title || 'Offer Details'}
        testID="offer-detail-sheet"
      >
        {offersVM.offer && (
          <SheetContentOffer
            imageProps={{
              imageUrl: offersVM.offer.imageUrl,
              accessibilityLabel: `${offersVM.offer.title} offer image`,
            }}
            offerDetailsProps={{
              offerTitle: offersVM.offer.title,
              offerDescription: offersVM.offer.description,
              buttonText: detailSheetButton.text,
              buttonVariant: detailSheetButton.variant,
              disabled: detailSheetButton.disabled,
              onOfferPress: handleDetailSheetButtonPress,
              finePrintText: [
                generateOfferTerms(offersVM.offer, restaurantName, merchantConfig),
                getRemainingUsesText(offersVM.offer),
              ].filter(Boolean).join('\n\n'),
              // Field only in the add state — hidden for LOGIN/REMOVE/UNAVAILABLE (matches mobile + spec §4b table)
              requiresCode: requiresCode(offersVM.offer) && detailSheetButton.text === 'ADD TO ORDER',
              codeValue: offersVM.enteredCode,
              onCodeChange: (v: string) => { offersVM.setEnteredCode(v); offersVM.setCodeError(null) },
              codeError: offersVM.codeError ?? undefined,
            }}
            testID="offer-detail-content"
            accessibilityLabel={`${offersVM.offer.title} offer details`}
          />
        )}
      </Sheet>

      {/* Step 1: Offer Product Selection Sheet (multi-product choices) */}
      <Sheet
        isOpen={isOfferFlowSheetOpen && !isSingleProduct}
        onClose={handleOfferFlowCancel}
        title={sheetTitle}
        testID="offer-product-selection-sheet"
      >
        <SheetContentProductSelection
          headingText={
            offersVM.offerFlowState === 'required_products'
              ? 'Choose a required item:'
              : 'Pick your free item:'
          }
          products={productChoices}
          onProductSelect={handleProductSelect}
          onBackPress={offersVM.currentProductIndex > 0 ? handleFlowGoBack : undefined}
          showSectionRequiredProduct={showSectionNav}
          sectionRequiredProductProps={
            showSectionNav
              ? {
                  requiredItemDescription: sectionItemDescription,
                  showBackwardButton: offersVM.currentProductIndex > 0,
                  showForwardButton: false,
                  showChangeItemButton: false,
                }
              : undefined
          }
          testID="offer-multi-product-selection"
        />
      </Sheet>

      {/* Step 2: Product Detail / Customization Sheet */}
      <Sheet
        isOpen={isProductDetailOpen}
        onClose={handleProductDetailClose}
        title={selectedOfferMenuItem?.name || 'Product Details'}
        testID="offer-product-detail-sheet"
      >
        {selectedOfferMenuItem && (
          <SheetContentProduct
            showSectionRequiredProduct={showSectionNav || showChangeItem}
            sectionRequiredProduct={
              (showSectionNav || showChangeItem)
                ? {
                    requiredItemDescription: (showSectionNav || showChangeItem) ? sectionItemDescription : undefined,
                    showBackwardButton: false,
                    showForwardButton: false,
                    showChangeItemButton: showChangeItem,
                    onChangeItemPress: handleProductDetailClose,
                  }
                : undefined
            }
            sectionProductInformation={{
              productName: selectedOfferMenuItem.name,
              description: selectedOfferMenuItem.description || '',
              currentPrice: isFreeStep
                ? (offerDiscountPct >= 100 ? 'FREE' : `$${(selectedOfferMenuItem.price * (1 - offerDiscountPct / 100)).toFixed(2)}`)
                : `$${selectedOfferMenuItem.price.toFixed(2)}`,
              originalPrice: isFreeStep ? `$${selectedOfferMenuItem.price.toFixed(2)}` : undefined,
              imageUrl: selectedOfferMenuItem.imageUrl,
            }}
            optionGroups={offerOptionGroups}
            sectionAddToOrder={{
              price: isFreeStep
                ? (offerDiscountPct >= 100 ? 'FREE' : `$${(offerProductTotalPrice * (1 - offerDiscountPct / 100)).toFixed(2)}`)
                : `$${offerProductTotalPrice.toFixed(2)}`,
              quantity: offerProductQuantity,
              onQuantityChange: setOfferProductQuantity,
              isFavorited: false,
              onFavoriteToggle: () => {},
              missingRequirements: isOfferApplied ? undefined : offerMissingRequirements,
              onAddToOrder: isOfferApplied ? handleOfferRemove : handleOfferAddToOrder,
              ...(isOfferApplied
                ? {
                    buttonText: 'REMOVE OFFER',
                    buttonStyle: { backgroundColor: '#dc2626' },
                  }
                : {}),
            }}
            testID="offer-product-detail-content"
          />
        )}
      </Sheet>

      {/* Offer warning alerts (one-offer conflict, time constraints, min purchase, etc.).
          Required-prop fallbacks: alertProps is Partial<AlertProps>; when the
          hook is inactive it returns { visible: false } and Alert renders null,
          so these placeholders are never displayed. Spread wins when active. */}
      <Alert
        title=""
        description=""
        primaryAction={{ text: '', onPress: () => {} }}
        {...offerAlert.alertProps}
      />
    </>
  )
}
